"use client";

import { useMemo, useState } from "react";
import { formOptions } from "@tanstack/react-form";
import { useTranslations } from "next-intl";

import { useAppForm } from "../shared/hooks/form";
import { SubmitButton } from "../shared/SubmitButton";
import { emailValidator, optionalString, requiredString } from "../shared/validators";
import { createValidationMessages, enrichWithUTMs } from "../shared/utils";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";
import type { HubSpotSubmitResult } from "@/app/_api/hubspot/shared/types";

import { TALENT_REPRESENTATION_FORM_DEFAULTS, fieldConfig } from "./constants";
import type { TalentRepresentationPayload } from "./types";

const formOpts = formOptions({
  defaultValues: TALENT_REPRESENTATION_FORM_DEFAULTS,
});

export interface TalentRepresentationFormProps {
  onSubmit: (payload: TalentRepresentationPayload) => Promise<HubSpotSubmitResult>;
  onSuccess?: () => void;
}

export function TalentRepresentationForm({ onSubmit, onSuccess }: TalentRepresentationFormProps) {
  const t = useTranslations("FormsClientKeys.talent_representation_form");
  const tValidation = useTranslations("ClientKeys.forms.validation");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { captureEvent } = useAnalytics();

  const messages = useMemo(() => createValidationMessages(tValidation), [tValidation]);
  const validators = useMemo(
    () => ({
      talentName: requiredString(messages, 200),
      representativeName: requiredString(messages, 200),
      representativeEmail: emailValidator(messages),
      talentSocialLinks: optionalString(messages, 2000),
      shortBio: optionalString(messages, 5000),
    }),
    [messages],
  );

  const form = useAppForm({
    ...formOpts,
    onSubmit: async ({ value }) => {
      setSubmitError(null);

      const payload: TalentRepresentationPayload = enrichWithUTMs(value);

      try {
        const result = await onSubmit(payload);
        if (result.success) {
          captureEvent(EventName.mw_talent_representation_form_submitted, {});
          onSuccess?.();
        } else {
          setSubmitError(result.error || t("errors.submission_failed"));
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to submit talent representation form");
        setSubmitError(t("errors.submission_failed"));
        throw error;
      }
    },
  });

  return (
    <div className='tw-w-full'>
      <form
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField
          name={fieldConfig.talentName.name}
          validators={{ onMount: validators.talentName, onChange: validators.talentName }}
        >
          {field => (
            <field.Input
              name={fieldConfig.talentName.name}
              label={t("fields.talent_name.label")}
              placeholder={t("fields.talent_name.placeholder")}
              required={fieldConfig.talentName.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.representativeName.name}
          validators={{ onMount: validators.representativeName, onChange: validators.representativeName }}
        >
          {field => (
            <field.Input
              name={fieldConfig.representativeName.name}
              label={t("fields.representative_name.label")}
              placeholder={t("fields.representative_name.placeholder")}
              required={fieldConfig.representativeName.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.representativeEmail.name}
          validators={{ onMount: validators.representativeEmail, onChange: validators.representativeEmail }}
        >
          {field => (
            <field.Input
              name={fieldConfig.representativeEmail.name}
              label={t("fields.representative_email.label")}
              placeholder={t("fields.representative_email.placeholder")}
              type='email'
              required={fieldConfig.representativeEmail.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.talentSocialLinks.name}
          validators={{ onMount: validators.talentSocialLinks, onChange: validators.talentSocialLinks }}
        >
          {field => (
            <field.Input
              name={fieldConfig.talentSocialLinks.name}
              label={t("fields.talent_social_links.label")}
              placeholder={t("fields.talent_social_links.placeholder")}
              required={fieldConfig.talentSocialLinks.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.shortBio.name}
          validators={{ onMount: validators.shortBio, onChange: validators.shortBio }}
        >
          {field => (
            <field.Textarea
              name={fieldConfig.shortBio.name}
              label={t("fields.short_bio.label")}
              placeholder={t("fields.short_bio.placeholder")}
              required={fieldConfig.shortBio.required}
              rows={5}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={state => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
          {({ canSubmit, isSubmitting }) => (
            <SubmitButton
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              submitLabel={t("buttons.submit")}
              submittingLabel={t("buttons.submitting")}
              submitError={submitError}
            />
          )}
        </form.Subscribe>
      </form>
    </div>
  );
}
